import {DataStatus} from 'platform/components';

import {useCreateVehicleEarningPresetMutation, useListVehicleEarningsQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {RequiredTestIdProps} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {CreateTransactionPresetForm} from '../CreateTransactionPresetForm';

interface CreateVehicleEarningTransactionPresetProps extends RequiredTestIdProps {
  vehicleId: string;
  onDiscard?: VoidFunction;
  onSuccess?: VoidFunction;
}

type SubmitHandler = Parameters<Parameters<typeof CreateTransactionPresetForm>[0]['onSubmit']>[0];

export function CreateVehicleEarningTransactionPreset(
  props: CreateVehicleEarningTransactionPresetProps
) {
  const {
    data: earnings,
    isLoading: isEarningsLoading,
    isError: hasEarningsError,
  } = useListVehicleEarningsQuery({vehicleId: props.vehicleId, type: 'manual'});

  const [createVehicleEarningPreset] = useCreateVehicleEarningPresetMutation();

  const onSubmit = (data: SubmitHandler) =>
    createVehicleEarningPreset({
      createVehicleEarningPresetRequestBody: {
        vehicleId: props.vehicleId,
        earningIds: data.transactionsIds,
        name: data.name,
      },
    })
      .unwrap()
      .then(() => {
        props.onSuccess?.();
      })
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isEarningsLoading} isError={hasEarningsError}>
      <CreateTransactionPresetForm
        vehicleId={props.vehicleId}
        data-testid={props['data-testid']}
        onSubmit={onSubmit}
        onDiscard={props.onDiscard}
        label={i18n.t('entity.earningsCosts.labels.preset')}
        list={
          earnings?.map((earning) => ({
            label: earning.name,
            value: earning.id,
          })) ?? []
        }
        helperText={i18n.t('entity.earnings.labels.createPresetHelper')}
      />
    </DataStatus>
  );
}
